import React, { useState, useEffect } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Box, Typography, Avatar } from "@mui/material";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";
import Button from "@mui/material/Button";
import { red, green } from "@mui/material/colors";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import StockAnalyzerTabs from "./StockAnalyzerTabs";
import { fetchCompanyInfo } from "../api/companyInfo";
import CircularProgress from "@mui/material/CircularProgress";
import ExtendedCompanyInfo from "./ExtendedCompanyInfo";
// import ColorThief from "colorthief";

function CustomBreadcrumbs({ company }) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const breadcrumbs = [
		<Link
			component={RouterLink}
			variant="body2"
			underline="hover"
			key="1"
			color="inherit"
			to="/"
		>
			Home
		</Link>,
		<Link
			component={RouterLink}
			underline="hover"
			key="2"
			color="inherit"
			to="/stock-analyzer/AAPL/key-indicators"
			variant="body2"
		>
			Stock Analyzer
		</Link>,
		<Typography
			key="3"
			variant="body2"
			sx={{ color: "text.primary", marginTop: 0.1 }}
		>
			{company}
		</Typography>,
	];

	return (
		<Breadcrumbs
			separator="›"
			aria-label="breadcrumb"
			sx={{
				// marginTop: "4.05rem",
				marginTop: isMobile
					? `calc(61px + 0.3rem)`
					: `calc(65px + 0.3rem)`,
				marginLeft: "0.3rem",
			}}
		>
			{breadcrumbs}
		</Breadcrumbs>
	);
}

function CompanyInfo({ data }) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	// Company data
	// const company = {
	// 	name: "Apple Inc.",
	// 	ticker: "AAPL",
	// 	logo: "https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg", // Sample logo URL for Apple
	// 	exchange: "NASDAQ",
	// 	currency: "USD",
	// };

	// Function to remove punctuation from the end of a string
	const removePunctuation = (str) => {
		return str.replace(/[,!?;:]*$/, ""); // Remove punctuation from the end of the string
	};

	return (
		<Box
			component="section"
			sx={{
				// p: 2,
				// border: '1px dashed grey',
				display: "flex",
				alignItems: "center",
				// padding: 2,
				// borderRadius: 2,
				// boxShadow: 2,
				backgroundColor: "background.paper",
				marginTop: isMobile ? "2rem" : "3rem",
				justifyContent: "center",
			}}
		>
			{/* Circular Company Logo */}
			<Avatar
				alt={data.companyName}
				src={data.image}
				sx={{
					width: 60, // Size of the logo
					height: 60,
					marginRight: 2,
					bgcolor: "gray",
					boxShadow: isMobile ? 3 : 5,
				}}
				imgProps={{
					style: {
						// objectFit: "contain", // Ensure the image fits inside
						width: 40,
						height: 40,
						borderRadius: 7,
					},
				}}
			/>

			{/* Company Information */}
			<Box>
				<Typography
					variant={isMobile ? "h6" : "h6"}
					component="div"
					sx={{ fontWeight: "bold" }}
				>
					{removePunctuation(
						data.companyName.split(" ").slice(0, 2).join(" ")
					)}{" "}
					({data.symbol})
				</Typography>
				<Typography variant="body2" color="text.secondary">
					Exchange: {data.exchangeShortName}
				</Typography>
				<Typography variant="body2" color="text.secondary">
					Currency: {data.currency}
				</Typography>
			</Box>
		</Box>
	);
}

function StockInfo({ data }) {
	const [isTracked, setIsTracked] = useState(false); // Track follow state
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const handleTrackButtonClick = () => {
		setIsTracked((prevState) => !prevState); // Toggle between true/false
	};

	// stock data, change in %
	// const stock = {
	// 	price: 236.48,
	// 	change: 0.63,
	// 	currency: "$",
	// };
	const getCurrencySymbol = (currencyCode) => {
		// Create a NumberFormat object with the given currency code
		const formatter = new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: currencyCode,
		});

		// Format a dummy number to get the currency symbol (e.g., $ for USD)
		const formatted = formatter.format(0); // 0 doesn't matter, we just need the symbol
		return formatted.replace(/\d|\./g, "").trim(); // Remove digits and trim extra spaces
	};

	return (
		<Box
			component="section"
			sx={{
				paddingTop: 2,
				//   paddingBottom: 2,
				//   border: '1px dashed grey',
				display: "flex",
				alignItems: "center",
				//   padding: 2,
				//   borderRadius: 2,
				// boxShadow: 2,
				backgroundColor: "background.paper",
				//   marginTop: '0rem',
				justifyContent: "center",
			}}
		>
			{/* Company Information */}
			<Box sx={{ display: "flex" }}>
				<Typography
					variant={isMobile ? "h6" : "h6"}
					component="div"
					sx={{ marginRight: "0.5rem", fontWeight: "bold" }}
				>
					{getCurrencySymbol(data.currency)}
					{data.price.toFixed(2)}
				</Typography>
				<Typography
					variant="body1"
					component="div"
					sx={{
						color:
							data.changes > 0
								? green[600]
								: data.changes === 0
								? "inherit"
								: red[600],
						fontWeight: "bold",
					}}
				>
					{data.changes > 0 ? "+" : data.changes === 0 ? "" : "-"}
					{getCurrencySymbol(data.currency)}
					{Math.abs(data.changes).toFixed(2)} (
					{Math.abs(
						(
							(data.changes / (data.price - data.changes)) *
							100
						).toFixed(2)
					)}
					%)
				</Typography>
			</Box>
			<Box sx={{ width: isMobile ? 0 : "2rem" }}></Box>

			{/* Company Information */}
			<Box sx={{}}>
				<Button
					disableElevation
					variant="contained"
					size={isMobile ? "small" : "medium"}
					// variant={ isFollowed ? "contained" : "outlined" }
					// startIcon={ <StarIcon /> }
					startIcon={isTracked ? <StarIcon /> : <StarBorderIcon />}
					sx={{
						marginLeft: "1rem",
						bgcolor: "primary.main",
						borderRadius: "25px",
						// backgroundColor: blue[500],
						textTransform: "none",
					}}
					onClick={handleTrackButtonClick}
				>
					Track
				</Button>
			</Box>
		</Box>
	);
}

export default function StockAnalyzer2() {
	const { symbol } = useParams();
	const [stockData, setStockData] = useState(null);

	useEffect(() => {
		(async function getStockData() {
			try {
				const data = await fetchCompanyInfo(symbol);
				setStockData(data[0]);
			} catch (error) {
				console.error("Error fetching company info:", error);
			}
		})();
	}, [symbol]);

	// change to circular progress
	if (!stockData)
		return (
			<Box
				sx={{
					display: "flex",
					marginTop: "15rem",
					justifyContent: "center",
				}}
			>
				<CircularProgress />
			</Box>
		);

	return (
		<>
			<CustomBreadcrumbs company={stockData.symbol}></CustomBreadcrumbs>
			<CompanyInfo data={stockData}></CompanyInfo>
			<StockInfo data={stockData}></StockInfo>
			<ExtendedCompanyInfo data={stockData}></ExtendedCompanyInfo>
			<StockAnalyzerTabs></StockAnalyzerTabs>
		</>
	);
}
